import { cx } from '@orn/shared-web/utils/cx';
import formatPacificDateTime from '@orn/shared-web/utils/datetime/formatPacificDateTime';
import * as React from 'react';

import { FamilyRole, UserStatus } from '../../__generated__/globalTypes';
import InspectLink from '../../components/InspectLink';
import ChannelHealthBadge from '../ChannelHealth/ChannelHealthBadge';
import { CHANNEL_NAMES, channelHealthDetail } from '../ChannelHealth/channelHealthView';

import { Absent, Field, FieldList, Timestamp } from './AccountFields';
import { ToolLinks } from './AccountLinks';
import {
  INBOUND_CHANNEL_LABELS,
  formatFamilyRole,
  householdOwnerId,
  formatOutreachHistory,
  formatOutreachPolicy,
  formatPersonName,
  formatSmsConsent,
  formatUserStatus,
  memberToolLinks,
} from './accountsView';
import RecentNotificationsPanel from './RecentNotificationsPanel';

import type { AdminGetFamilyAccountQuery } from './__generated__/AccountsScene.gql';

type LinkedUser = NonNullable<AdminGetFamilyAccountQuery['adminGetFamilyAccount']>['linkedUsers'][number];

interface ILinkedUsersPanelProps {
  linkedUsers: LinkedUser[];
}

/**
 * Every user account in the selected household, caregivers first, one block
 * per member.
 *
 * Deliberately not a table: a member carries around a dozen attributes, and a
 * dozen columns is a horizontal scroll an operator reads one row of at a time.
 * The blocks stay dense by sharing one label column. Each member links out to
 * their Assistant threads and memory, and an active caregiver to impersonation.
 */
const LinkedUsersPanel = ({ linkedUsers }: ILinkedUsersPanelProps) => {
  const ownerId = householdOwnerId(linkedUsers);

  return (
    <section className="flex min-w-0 flex-col gap-3">
      {linkedUsers.length === 0 ? (
        <div className="text-fg-tertiary text-xs italic">No user accounts in this household.</div>
      ) : (
        <div className="flex flex-col gap-4">
          {linkedUsers.map((member) => (
            <LinkedUserBlock key={member.id} member={member} isOwner={member.id === ownerId} />
          ))}
        </div>
      )}
    </section>
  );
};

interface ILinkedUserBlockProps {
  member: LinkedUser;
  /** Whose sign-up created the household; the pill rides ahead of the role. */
  isOwner: boolean;
}

const LinkedUserBlock = ({ member, isOwner }: ILinkedUserBlockProps) => {
  // Codegen renders a nullable field as `T | null | undefined`; the panel only
  // ever distinguishes present from absent, so the two collapse here.
  const consent = formatSmsConsent(member.smsConsentState ?? null);
  const phoneNumber = member.phoneNumber ?? null;
  const lastLoginAt = member.lastLoginAt ?? null;
  const outreachHistory = formatOutreachHistory(member.outreachHistory);
  // A loved one's account exists to be reached, never to be signed into, so
  // the sign-in rows are not "never" for them — they do not apply.
  const canSignIn = member.familyRole !== FamilyRole.Dependent;

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex items-baseline gap-2">
        <span className="text-fg-primary text-sm font-semibold">{formatPersonName(member, member.email)}</span>
        {isOwner && <Badge>Owner</Badge>}
        <Badge>{formatFamilyRole(member.familyRole)}</Badge>
        {member.status !== UserStatus.Active && <Badge tone="warning">{formatUserStatus(member.status)}</Badge>}
        <ToolLinks label={`${member.displayName}'s tools`} links={memberToolLinks(member)} className="ml-auto" />
      </div>

      <FieldList>
        <Field label="User id" mono>
          <InspectLink id={member.id} />
        </Field>
        <Field label="Auth">
          {!canSignIn ? <Absent>N/A</Absent> : member.googleAuthLinked ? 'Google' : 'Password'}
        </Field>
        <Field label="Email" mono>
          {member.email} <Verification verifiedAt={member.emailVerifiedAt ?? null} />
        </Field>
        <Field label="Phone" mono>
          {phoneNumber === null ? (
            <Absent>None</Absent>
          ) : (
            <>
              {phoneNumber} <Verification verifiedAt={member.phoneNumberVerifiedAt ?? null} />
              {consent !== null && <span className="text-fg-tertiary ml-1 text-xs">· {consent}</span>}
            </>
          )}
        </Field>
        <Field label="Timezone">{member.outreachTimezone}</Field>
        <Field label="Last web login">
          {!canSignIn ? (
            <Absent>N/A</Absent>
          ) : lastLoginAt === null ? (
            <Absent>Never</Absent>
          ) : (
            <Timestamp instant={lastLoginAt} />
          )}
        </Field>
        <Field label="Last reply to assistant">
          {member.lastInboundMessages.length === 0 ? (
            <Absent>Never</Absent>
          ) : (
            <div className="flex flex-col gap-0.5">
              {member.lastInboundMessages.map((message) => (
                <span key={message.channel}>
                  <span className="text-fg-tertiary text-xs uppercase tracking-wider">
                    {INBOUND_CHANNEL_LABELS[message.channel]}
                  </span>{' '}
                  <Timestamp instant={message.sentAt} />
                </span>
              ))}
            </div>
          )}
        </Field>
      </FieldList>

      {/*
        Everything about reaching this member sits under one heading: the policy
        that governs a send, the counts of what landed, and the detail behind
        those counts. The table carries no label of its own because it is that
        detail rather than a fourth subject.
      */}
      <div className="flex flex-col gap-1">
        {/* Weight and spacing carry the heading; a rule here read as a divider
            between members rather than as the top of a subsection. */}
        <h3 className="text-fg-secondary mt-2 text-xs font-semibold uppercase tracking-wider">Notifications</h3>
        <FieldList>
          {member.channelHealth.map((health) => {
            const detail = channelHealthDetail(health);
            return (
              <Field key={health.channel} label={CHANNEL_NAMES[health.channel]}>
                <ChannelHealthBadge health={health} />
                {detail !== null && <span className="text-fg-tertiary ml-2">{detail}</span>}
              </Field>
            );
          })}
          <Field label="Config">{formatOutreachPolicy(member)}</Field>
          <Field label="History">{outreachHistory === null ? <Absent>None delivered</Absent> : outreachHistory}</Field>
        </FieldList>

        <RecentNotificationsPanel notifications={member.recentNotifications} userId={member.id} />
      </div>
    </div>
  );
};

interface IVerificationProps {
  verifiedAt: string | null;
}

/**
 * Whether the endpoint was ever proved, with the date behind a hover.
 *
 * Null is the unverified state, but for a phone number it also covers "proved,
 * then replaced" — the column is reset whenever the number changes — so the
 * word is "unverified" rather than "never verified".
 */
const Verification = ({ verifiedAt }: IVerificationProps) =>
  verifiedAt === null ? (
    <span className="text-fg-warning-primary text-[10px] uppercase tracking-wide">unverified</span>
  ) : (
    <span
      className="text-fg-success-primary text-[10px] uppercase tracking-wide"
      title={`Verified ${formatPacificDateTime(verifiedAt)}`}
    >
      verified
    </span>
  );

interface IBadgeProps {
  tone?: 'neutral' | 'warning';
  children: React.ReactNode;
}

const Badge = ({ tone = 'neutral', children }: IBadgeProps) => (
  <span
    className={cx(
      'border-border-primary whitespace-nowrap rounded-sm border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
      tone === 'warning' ? 'text-fg-warning-primary' : 'text-fg-tertiary'
    )}
  >
    {children}
  </span>
);

export default LinkedUsersPanel;
